import React from 'react';
import { useGetSupplierPrepaymentApContractorsLookupQuery } from '../Api/bankDocumentsApi';
import {
  mapApContractorLookup,
  type BankDocumentApContractorLookup,
} from './apContractorsLookupMapper';
import { bankDocumentsLogger } from '../Utils/bankDocumentsLogger';

interface BankDocumentApContractorsLookupArgs {
  searchTerm?: string;
}

export const useGetBankDocumentApContractorsLookupQuery = (
  args: BankDocumentApContractorsLookupArgs = {},
  options?: { skip?: boolean }
): {
  apContractors: BankDocumentApContractorLookup[];
  isLoading: boolean;
  isFetching: boolean;
} => {
  const endpoint = '/supplier-prepayments/ap-contractors/lookup';
  const normalizedSearchTerm = args.searchTerm?.trim();

  React.useEffect(() => {
    bankDocumentsLogger.debug('[BankDocuments] AP contractors lookup endpoint', {
      endpoint,
      searchTerm: normalizedSearchTerm ?? null,
    });
  }, [endpoint, normalizedSearchTerm]);

  const { data, isLoading, isFetching } = useGetSupplierPrepaymentApContractorsLookupQuery(
    normalizedSearchTerm || undefined,
    options
  );

  const apContractors: BankDocumentApContractorLookup[] = (data ?? [])
    .map(mapApContractorLookup)
    .filter((item): item is BankDocumentApContractorLookup => item !== undefined)
    .sort((a, b) => a.name.localeCompare(b.name, 'pl'));

  React.useEffect(() => {
    if (isLoading) {
      return;
    }

    bankDocumentsLogger.debug('[BankDocuments] AP contractors lookup response', {
      endpoint,
      searchTerm: normalizedSearchTerm ?? null,
      count: apContractors.length,
    });
  }, [apContractors.length, endpoint, isLoading, normalizedSearchTerm]);

  return { apContractors, isLoading, isFetching };
};
